import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { HasCategoryContext, withCategory } from "@SupplyLand/Overrides/Components/CategoryContext";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: { category: Category; products: ProductDto[] }) => {
    const subCategories: Readonly<Category>[] = [];
    ownProps.category?.subCategoryIds?.forEach(subCategoryId => {
        const cat = getCategoryState(state, subCategoryId).value;
        if (cat !== undefined) {
            subCategories.push(cat);
        }
    });
    return {
        mainCategory: ownProps.category,
        products: ownProps.products,
        subCategories,
    };
};

export interface CategoryDetailSubCategoriesStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    titleText?: TypographyProps;
    heroContainer?: GridContainerProps;
    productCategoryContainer?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    heroSubCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    heroInnerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    heroSubCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    heroSubCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: TypographyProps;
    heroSubCategoryNameLink?: LinkPresentationProps;
    heroSubCategoryDescription?: TypographyProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    wrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    heroContainer: {
        gap: 12,
        offsetCss: css`
            align-items: stretch;
        `,
    },
    titleText: {
        variant: "h1",
        css: css`
            font-size: 32px !important;
        `,
    },
    productCategoryContainer: {
        gap: 12,
        offsetCss: css`
            align-items: baseline;
        `,
    },
    heroSubCategoryItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            align-self: stretch;
            align-items: stretch;
            margin-bottom: 30px !important;
        `,
    },
    heroInnerContainer: {
        gap: 0,
        css: css`
            justify-content: center;
        `,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    heroSubCategoryImageClickable: {
        css: css`
            justify-content: center;
            display: flex;
            flex-direction: column;
            width: 100%;
            outline: 1px solid black !important;
            & :hover {
                box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
            }
        `,
    },
    heroSubCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-top: 4px;
            padding-bottom: 4px;
            background: black;
            max-height: 30px;
            width: 100%;
            text-transform: uppercase;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    heroSubCategoryDescription: {
        css: css`
            color: white;
            font-weight: bold;
        `,
    },
    heroSubCategoryNameLink: {
        css: css`
            width: 100%;
            color: white;
        `,
        color: "white",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
                color: white;
            `,
        },
    },
    container: {
        gap: 0,
        offsetCss: css`
            align-items: stretch;
            margin: -6px;
        `,
    },
    subCategoryItem: {
        width: [6, 6, 4, 4, 3],
        css: css`
            align-self: stretch;
            align-items: stretch;
        `,
    },
    innerContainer: {
        css: css``,
        gap: 0,
    },
    subCategoryImageClickable: {
        css: css`
            padding: 20px 20px 20px 20px;
            background-color: whitesmoke;
            justify-content: center;
            display: flex;
            flex-direction: column;
            margin: 0 8px 0 8px;
            width: 100%;
            & :hover {
                box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
            }
        `,
    },
    subCategoryImage: {
        css: css`
            margin: 10px;
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            color: white;
            margin: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
            text-align: center;
            word-wrap: break-word;
            font-weight: bold;
        `,
        color: "text.main",
    },
};

interface CategoryProduct {
    name?: string;
    imagePath?: string;
    url?: string;
    altText?: string;
    isProduct: boolean;
    sortOrder: number;
    id: string;
}

const styles = categoryDetailSubCategoriesStyles;

const CategorySubCategoriesAndProducts: React.FC<Props> = props => {
    if (!props.category || !props.category.subCategoryIds || props.category.subCategoryIds.length === 0) {
        return null;
    }
    return <SubCategorySection category={props.category} products={props.products} />;
};

const SubCategorySectionView = ({ mainCategory, products, subCategories }: ReturnType<typeof mapStateToProps>) => {
    if (!mainCategory) {
        return null;
    }

    // const {
    //     fields: { showImages },
    // } = props;
    const categoryProducts: CategoryProduct[] = [];

    subCategories?.forEach(subCategory => {
        const categoryProduct = {
            id: subCategory.id,
            name: subCategory.shortDescription,
            url: subCategory.path,
            imagePath: subCategory.smallImagePath,
            isProduct: false,
            sortOrder: subCategory.sortOrder,
            altText: subCategory.imageAltText,
        };

        categoryProducts.push(categoryProduct);
    });

    if (products) {
        products?.forEach(product => {
            const categoryProduct = {
                name: product.shortDescription,
                url: product.productDetailUrl,
                imagePath: product.smallImagePath,
                isProduct: true,
                sortOrder: product.sortOrder,
                id: product.id,
                altText: product.altText,
            };

            categoryProducts.push(categoryProduct);
        });
    }
    categoryProducts.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

    const subCategoryCount = categoryProducts.length;
    let topThreeSubCategories: CategoryProduct[] = [];
    let remainingSubCategories: CategoryProduct[];

    if (subCategoryCount > 3) {
        topThreeSubCategories = categoryProducts.slice(0, 3);
        remainingSubCategories = categoryProducts.slice(3);
    } else {
        remainingSubCategories = categoryProducts;
    }

    return (
        <GridWrapper {...styles.wrapper}>
            {mainCategory.name && (
                <GridContainer>
                    <GridItem width={12}>
                        <Typography {...styles.titleText}>{mainCategory.name}</Typography>
                    </GridItem>
                </GridContainer>
            )}
            {mainCategory.htmlContent && (
                <GridContainer {...styles.heroContainer}>
                    <GridItem width={12}>
                        <Typography>{parse(mainCategory.htmlContent, parserOptions)}</Typography>
                    </GridItem>
                </GridContainer>
            )}
            <Hidden below="md">
                <GridContainer {...styles.heroContainer}>
                    {topThreeSubCategories.length > 0 &&
                        topThreeSubCategories.map(category => (
                            <GridItem key={category.id} {...styles.heroSubCategoryItem}>
                                <HeroSubCategoryLinkView category={category} />
                            </GridItem>
                        ))}
                </GridContainer>
                <GridContainer {...styles.productCategoryContainer}>
                    {remainingSubCategories.map(categoryProduct => (
                        <GridItem key={categoryProduct.id} {...styles.subCategoryItem}>
                            <ProductLinkView product={categoryProduct} />
                        </GridItem>
                    ))}
                </GridContainer>
            </Hidden>
            <Hidden above="sm">
                <GridContainer {...styles.productCategoryContainer}>
                    {categoryProducts.map(categoryProduct => (
                        <GridItem key={categoryProduct.id} {...styles.subCategoryItem}>
                            <ProductLinkView product={categoryProduct} />
                        </GridItem>
                    ))}
                </GridContainer>
            </Hidden>
        </GridWrapper>
    );
};

const ProductLinkView = ({ product, showImages }: ReturnType<any>) => {
    if (!product) {
        return null;
    }

    return (
        <Clickable href={product.url} {...styles.subCategoryImageClickable}>
            {product.imagePath && (
                <LazyImage src={product.imagePath} altText={product.altText} {...styles.subCategoryImage} />
            )}
            {/*                 </GridItem>
        <GridItem {...styles.subCategoryNameLinkItem}> */}
            <Typography
                {...styles.subCategoryNameLink}
                data-test-selector={`categoryDetailsSubCategoriesLink_${product.id}`}
            >
                {product.name}
            </Typography>
        </Clickable>
    );
};

const HeroSubCategoryLinkView = ({ category, showImages }: ReturnType<any> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <Clickable href={category.url} {...styles.heroSubCategoryImageClickable}>
            <GridItem {...styles.subCategoryImageItem}>
                {category.imagePath && (
                    <LazyImage src={category.imagePath} altText={category.imageAltText} {...styles.subCategoryImage} />
                )}
            </GridItem>
            <GridItem {...styles.heroSubCategoryNameLinkItem}>
                <Typography
                    {...styles.heroSubCategoryDescription}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.name}
                </Typography>
            </GridItem>
        </Clickable>
    );
};

const SubCategorySection = connect(mapStateToProps)(SubCategorySectionView);

const widgetModule: WidgetModule = {
    component: withCategory(CategorySubCategoriesAndProducts),
    definition: {
        group: "Categories",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
